<template>
  <v-app id="app">
    <v-navigation-drawer v-model="drawer" app clipped data-test-id="navigationDrawer">
      <v-list dense class="pb-15">
        <v-list-item link to="/">
          <v-list-item-action>
            <v-icon>mdi-home-variant-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('home.menu-title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/dashboard" v-if="isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-view-dashboard</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('dashboard.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/register" v-if="!isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-account-plus</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('registration.sign-up')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/login" v-if="!isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-login</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('common.login')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/pricelist" v-if="!isLoggedIn && !$nativeApp.isIOS() && isPremiumFeatureEnabled()">
          <v-list-item-action>
            <v-icon>mdi-format-list-bulleted-type</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('pricelist.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/profile" v-if="isLoggedIn">
          <v-list-item-action>
            <player-avatar v-if="$store.state.profile" :user="$store.state.profile"></player-avatar>
            <v-icon v-else>mdi-account-circle-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('profile.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/friends" v-if="isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-account-multiple</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('friends.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/friendshipRequests" v-if="isLoggedIn && numFriendshipRequests>0">
          <v-list-item-action>
            <v-badge
              color="accent"
              overlap
              :content="numFriendshipRequests"
              :value="numFriendshipRequests>0"
            >
              <v-icon>mdi-account-question</v-icon>
            </v-badge>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('friendshiprequests.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/gameInvitations" v-if="isLoggedIn && numGameInvitations>0">
          <v-list-item-action>
             <v-badge
              color="accent"
              overlap
              :content="numGameInvitations"
              :value="numGameInvitations>0"
            >
              <v-icon>mdi-email</v-icon>
            </v-badge>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('gameInvitations.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/game/new" v-if="canCreateNewGames">
          <v-list-item-action>
            <v-icon>mdi-soccer-field</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('createGame.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <games-list-group
          :games="preparedGames"
          :title="$t('common.competitions-in-preparation')"
          prepend-icon="mdi-creation"
          v-if="preparedGames.length > 0"
        />
        <games-list-group
          :games="activeGames"
          :title="$t('common.competitions-active')"
          prepend-icon="mdi-gamepad-square"
          v-if="activeGames.length > 0"
        />

        <v-list-item link to="/competitions/public" v-if="isLoggedIn && $store.state.env.global.listPublicCompetitions === true">
          <v-list-item-action>
            <v-icon>mdi-earth</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('publicGames.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/gamehistory" v-if="isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-history</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('gameHistory.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/coins" v-if="coinsMenuItemVisible">
          <v-list-item-action>
            <v-icon>mdi-circle-multiple-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('coins.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/shop" v-if="shopMenuItemVisible">
          <v-list-item-action>
            <v-icon>mdi-store</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$tenant.tenant === 'bfv' ? $t('shop.title-bfv') : $t('shop.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/premium" v-if="isLoggedIn && !$nativeApp.isIOS() && isPremiumFeatureEnabled()">
          <v-list-item-action>
            <v-icon>mdi-star-circle-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('premium.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/faq">
          <v-list-item-action>
            <v-icon>mdi-frequently-asked-questions</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('faqs.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/contact" v-if="isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-file-document-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('contact.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link @click="logout" v-if="isLoggedIn">
          <v-list-item-action>
            <v-icon>mdi-logout</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('common.logout')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item link to="/impress">
          <v-list-item-action>
            <v-icon>mdi-information-variant</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{$t('impress.title')}}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item link to="/devTools" v-if="$route.query.debug=='1'">
          <v-list-item-action>
            <v-icon>mdi-developer-board</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>DevTools</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app dense clipped-left>
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" data-test-id="navigationMenuToggle">
        <v-badge
          :content="overallBadge"
          :value="!drawer && overallBadge > 0"
          :overlap="true"
          color="accent"
        >
          <v-icon>mdi-menu</v-icon>
        </v-badge>
      </v-app-bar-nav-icon>
      <v-toolbar-title>
        <div class="d-flex align-center">
          <div class="shrink mt-1 hidden-xs-and-down headline">
            <span>{{$tenant.title}}<sup v-if="$tenant.title_sup">{{$tenant.title_sup}}</sup></span>
          </div>
        </div>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-img
        v-if="!hideTopRightLogo"
        :alt="$tenant.title + ' Logo'"
        class="shrink topRightLogo"
        contain
        :src="$tenant.headerLogo"
        transition="scale-transition"
      />
    </v-app-bar>

    <notification />

    <v-main :class="'appContent ' + $tenant.tenant">
      <v-progress-linear
        height="2px"
        style="z-index:2;position:fixed;"
        :indeterminate="$store.state.isLoading"
        :value="$store.state.isLoading ? 0 : 100"
        :background-opacity="0.3"
      ></v-progress-linear>

      <v-card color="success" v-if="$store.state.serviceWorker.updated">
        <v-card-text>
          <p>{{$t('common.new-app-available')}}</p>
          <v-btn color="" @click="reloadApp">
            <v-icon left>mdi-reload</v-icon>{{$t('common.update')}}
          </v-btn>
        </v-card-text>
      </v-card>

      <premium-dialog
        v-if="$store.state.showPremiumDialog"
        :visible="true"
        :closeAction="closePremiumDialog"
      />

      <coins-dialog
        v-if="$store.state.showCoinsDialog"
        :visible="true"
        :closeAction="closeCoinsDialog"
      />

      <online-ids-dialog
        v-if="$store.state.showOnlineIdsDialog"
        :visible="true"
        :closeAction="closeOnlineIdsDialog"
      />

      <user-country-dialog
        v-if="$store.state.showUserCountryDialog"
        :visible="true"
        :closeAction="closeUserCountryDialog"
      />

      <checkout-dialog
        v-if="$store.state.performSubscription"
        :title="'Abonnement buchen'"
        :visible="true"
        :name="$store.state.performSubscription.name"
        :description="$store.state.performSubscription.description"
        :price="$store.state.performSubscription.cost"
        :url="$store.state.performSubscription.url"
        :closeAction="abortSubscription"
      />

      <checkout-dialog
        v-if="$store.state.checkoutDialogData"
        :title="$store.state.checkoutDialogData.title"
        :visible="true"
        :name="$store.state.checkoutDialogData.name"
        :description="$store.state.checkoutDialogData.description"
        :price="$store.state.checkoutDialogData.price"
        :url="$store.state.checkoutDialogData.url"
        :closeAction="abortCheckoutDialog"
      />

      <subscribe-newsletter-dialog
          v-if="$store.state.newsletterModal"
          :visible="true"
      ></subscribe-newsletter-dialog>


      <v-container fluid class="pl-0 pr-0 mainView">
        <router-view></router-view>
      </v-container>

      <v-speed-dial
        v-if="isLoggedIn"
        v-model="menu"
        bottom
        absolute
        right
        fixed
        direction="top"
        transition="slide-y-reverse-transition"
      >
        <template v-slot:activator>
          <v-btn color="secondary" fab aria-label="Menü" data-test-id="speeddialButton">
            <v-icon v-if="menu" large>mdi-close</v-icon>
            <v-icon v-else large>mdi-menu-up</v-icon>
          </v-btn>
        </template>
        <v-btn
          fab
          small
          color="primary"
          @click="goBack"
          aria-label="Zurück"
          :disabled="!goBackAvailable()"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn
          fab
          small
          color="primary"
          @click="refreshData"
          :aria-label="$t('common.refresh')"
          :disabled="$store.state.isLoading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn fab small color="primary" to="/dashboard" aria-label="Dashboard">
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
        <v-btn
          v-if="numFriendshipRequests > 0"
          fab
          small
          to="/friendshipRequests"
          :aria-label="$t('friendshiprequests.title')"
        >
          <v-badge
            color="accent"
            overlap
            :content="numFriendshipRequests"
            :value="numFriendshipRequests>0"
          >
            <v-icon>mdi-account-question</v-icon>
          </v-badge>
        </v-btn>
        <v-btn
          v-if="numGameInvitations > 0"
          fab
          small
          to="/gameInvitations"
          aria-label="Wettkampf-Einladungen"
        >
          <v-badge
            color="accent"
            overlap
            :content="numGameInvitations"
            :value="numGameInvitations>0"
          >
            <v-icon>mdi-email</v-icon>
          </v-badge>
        </v-btn>
      </v-speed-dial>
    </v-main>

    <v-footer app>
      <span style="font-size:80%;">{{$tenant.footer.text}}</span>
    </v-footer>
  </v-app>
</template>

<script>
import eventBus, {
  EVENT_REFRESH_DATA,
  EVENT_REALTIME_ECHO_REGISTERED,
  EVENT_STORE_ENV_UPDATED
} from "@/plugins/eventbus";
import {
  ACTION_RELOAD_DATA,
  MUTATION_LOGOUT,
  MUTATION_SERVICE_WORKER_UPDATECONFIRMED,
  MUTATION_HIDE_PREMIUM_DIALOG,
  MUTATION_HIDE_CHECKOUT_DIALOG,
  MUTATION_HIDE_COINS_DIALOG,
  MUTATION_HIDE_ONLINE_IDS_DIALOG,
  MUTATION_HIDE_USER_COUNTRY_DIALOG,
  MUTATION_CANCELED_OPEN_SUBSCRIPION,
  MUTATION_DISMISS_NOTIFICATION
} from "@/plugins/store.js";
import Notification from "@/components/Notification";
import CheckoutDialog from "@/widgets/CheckoutDialog";
import gameTypeMixin from "@/mixins/gameTypeMixin";
import envMixin from "@/mixins/envMixin";
import PlayerAvatar from "@/widgets/PlayerAvatar";
import GamesListGroup from "@/widgets/GamesListGroup";
import PremiumDialog from "@/widgets/PremiumDialog";
import CoinsDialog from "@/widgets/CoinsDialog";
import OnlineIdsDialog from "@/widgets/OnlineIdsDialog";
import UserCountryDialog from "@/widgets/UserCountryDialog";
import SubscribeNewsletterDialog from './widgets/SubscribeNewsletterDialog'

export default {
  name: "App",
  mixins: [gameTypeMixin, envMixin],
  components: {
    SubscribeNewsletterDialog,
    Notification,
    CheckoutDialog,
    CoinsDialog,
    PlayerAvatar,
    PremiumDialog,
    OnlineIdsDialog,
    GamesListGroup,
    UserCountryDialog
  },
  props: {
    source: String,
    tenant: {
      type: String,
      default: undefined,
      required: false
    },
  },

  data: () => ({
    menu: false,
    drawer: null,
    cookieConsentEl: null
  }),

  watch: {
    drawer: function(val) {
      if (val == true) {
        // close drawer
        if (this.$vuetify.breakpoint.smAndDown) {
          this.$store.commit(MUTATION_DISMISS_NOTIFICATION);
        }
      }
    }
  },

  computed: {
    isLoggedIn() {
      return this.$store.state.isLoggedIn;
    },
    coinsMenuItemVisible() {
      let val = this.envGetUserKey('coins.enabled');
      let showCoins = this.$store.state.coinSystem.hideCoinFeature != true;
      return this.isLoggedIn && val === true && showCoins;
    },
    shopMenuItemVisible() {
      return this.coinsMenuItemVisible;
    },
    hideTopRightLogo() {
      return this.$route.path == "/" && this.isLoggedIn;
    },
    isLoginRoute() {
      // console.log(this.$route);
      return this.$route.name === "login";
    },
    preparedGames() {
      return this.$store.state.activeGames.filter(game => {
        return game.state == "created";
      });
    },
    activeGames() {
      return this.$store.state.activeGames.filter(game => {
        return game.state == "active" || game.state == "checkin";
      });
    },
    numFriendshipRequests() {
      return this.$store.state.friendshipRequests
        ? this.$store.state.friendshipRequests.length
        : 0;
    },
    numGameInvitations() {
      return this.$store.state.gameInvitations
        ? this.$store.state.gameInvitations.length
        : 0;
    },
    numUnreadChatConversations() {
      let listedGames = this.$store.state.activeGames.filter(game => {
        return game.state == "active" || game.state == "created";
      });
      var ret = 0;
      listedGames.forEach(game => {
        ret += game.yourUnreadChatMessages ? game.yourUnreadChatMessages : 0;
      });
      return ret;
    },
    overallBadge() {
      var ret = 0;
      ret += this.numFriendshipRequests;
      ret += this.numUnreadChatConversations;
      ret += this.numGameInvitations;
      return ret;
    },
    canCreateNewGames() {
      return this.envGetUserKey('permission.createGames') == true;
    },
  },

  created() {
    eventBus.$on("count", () => {
      // console.log("App received count event");
    });
  },
mounted() {
    this.$eventBus.$on(EVENT_REFRESH_DATA, this.onRefreshData);
    this.$eventBus.$on(EVENT_REALTIME_ECHO_REGISTERED, this.onEchoAvailable);
    this.$eventBus.$on(EVENT_STORE_ENV_UPDATED, this.onStoreEnvUpdated);
    this.embedCookieConsentScriptIfAvailable()
  },
  beforeDestroy() {
    // console.log("App beforeDestroy...");
    this.$eventBus.$off(EVENT_STORE_ENV_UPDATED, this.onStoreEnvUpdated);
    this.$eventBus.$off(EVENT_REALTIME_ECHO_REGISTERED, this.onEchoAvailable);
    this.$eventBus.$off(EVENT_REFRESH_DATA, this.onRefreshData);
  },
  methods: {
    reloadApp() {
      this.$store.commit(MUTATION_SERVICE_WORKER_UPDATECONFIRMED);
    },
    onEchoAvailable() {
      // console.log('Echo is now available')
      // Vue.prototype.$realtime.echo
    },
    onStoreEnvUpdated() {
      this.embedCookieConsentScriptIfAvailable()
    },
    embedCookieConsentScriptIfAvailable() {
      if (!this.cookieConsentEl && this.$store.state.env.global.userCentricsId) {
        let userCentrics = document.createElement('script')
        userCentrics.setAttribute('src', 'https://app.usercentrics.eu/browser-ui/latest/loader.js')
        userCentrics.setAttribute('data-settings-id', this.$store.state.env.global.userCentricsId)
        userCentrics.setAttribute('id', 'usercentrics-cmp')
        userCentrics.setAttribute('data-tcf-enabled', '1')

        this.cookieConsentEl = document.head.appendChild(userCentrics)
      } else {
        // consent script element is not updated e.g. the data-settings-id attribute (this is not needed)
      }
    },
    refreshData() {
      this.$eventBus.$emit(EVENT_REFRESH_DATA);
    },
    onRefreshData() {
      console.log("Refresh triggered");
      this.$store.dispatch(ACTION_RELOAD_DATA);
    },
    goBackAvailable() {
      return window.history.length > 1;
    },
    goBack() {
      this.$router.go(-1);
    },
    logout() {
      this.$store.commit(MUTATION_LOGOUT);
      this.$router.push("/");
    },
    closePremiumDialog() {
      this.$store.commit(MUTATION_HIDE_PREMIUM_DIALOG);
    },
    closeCoinsDialog() {
      this.$store.commit(MUTATION_HIDE_COINS_DIALOG);
    },
    closeOnlineIdsDialog() {
      this.$store.commit(MUTATION_HIDE_ONLINE_IDS_DIALOG);
    },
    closeUserCountryDialog() {
      this.$store.commit(MUTATION_HIDE_USER_COUNTRY_DIALOG);
    },
    abortCheckoutDialog() {
      this.$store.commit(MUTATION_HIDE_CHECKOUT_DIALOG);
    },
    abortSubscription() {
      this.$store.commit(MUTATION_CANCELED_OPEN_SUBSCRIPION);
    }
  }
};
</script>
<style>
.portal {
  color: var(--v-secondary-lighten1);
}
.help-text-small {
  color: var(--v-secondary-lighten4) !important;
}
.notice {
  color: var(--v-secondary-lighten4);
}
.v-application .container .headline,
.v-application .v-dialog .headline {
  text-shadow: 1px 2px 6px var(--v-primary-darken1);
}
.v-application.theme--dark .container .headline,
.v-application.theme--dark .v-dialog .headline {
 color: whitesmoke;
}

.v-application .container .subtitle-1,
.v-application .v-dialog .subtitle-1 {
  font-size: 1.2rem !important;
  text-shadow: 1px 2px 6px var(--v-primary-base);
}

.v-application.theme--dark .container .subtitle-1,
.v-application.theme--dark .v-dialog .subtitle-1 {
  color: whitesmoke;
}

.v-application .app-primary-light {
  text-shadow: 1px 2px 6px var(--v-primary-base);
}

/** iOS Workaround: disable horizontal scrolling. See #1169 */
@supports (-webkit-touch-callout: none) {
  .v-application {
    overflow-x: hidden;
  }
}

.premium-icon {
  color: var(--v-accent-lighten4);
  margin-right: 0px;
  text-shadow: 0 0 5px var(--v-accent-darken1), 0 0 10px var(--v-accent-darken1),
    0 0 15px var(--v-accent-darken1), 0 0 20px var(--v-accent-darken1),
    0 0 25px var(--v-accent-darken1), 0 0 30px var(--v-accent-darken1),
    0 0 35px var(--v-accent-darken1);
}
.green-glow {
  text-shadow: 0 0 5px var(--v-primary-darken4), 0 0 10px var(--v-primary-darken4),
  0 0 15px var(--v-primary-base), 0 0 20px var(--v-primary-base),
  0 0 25px var(--v-primary-darken4), 0 0 30px var(--v-primary-darken1),
  0 0 35px var(--v-primary-darken1);
}
.v-application.theme--dark .v-btn.primary .v-btn__content {
  color: black !important;
}
/** workaround for iOS bug #1100 */
.v-application.theme--dark .iOSWorkaroundBlack {
  color: black !important;
}
.v-application.theme--dark .v-list-item.primary .v-list-item__icon i {
  color: black;
}
.v-application.theme--dark .v-list-item.primary .v-list-item__content {
  color: black;
}
@media (prefers-color-scheme: dark) {
  img {
    opacity: 1;
  }
  .v-image__image {
    opacity: 1;
  }
}
/* TODO: This should be dynamically generated later */
main.appContent.bfv::before {
  content: "";
  background-color: red;
  position: absolute;
  left: 0;
  top: 0;
  background: url("https://d2f64mmirxruhh.cloudfront.net/app/assets/backgrounds/stadium-black-highlight.jpg") 15% 50%;
  background-size: cover;
  background-repeat: no-repeat;
  width: 100%;
  height: 100%;
  max-height: 1024px;
  opacity: 0.6;
}
</style>

<style scoped>
.topRightLogo {
  width: 60px;
  margin-top: 2.1em;
}
@media (min-width: 768px) {
  .topRightLogo {
    width: 80px;
    margin-top: 3.3em;
  }
}
.theme--dark .appContent {
  background-color: black;
}
@media (min-width: 1264px) {
  .container.mainView {
    max-width: 1185px;
  }
}
</style>
